import { useEffect, useRef, useState } from 'react'
import styles from './styles.module.css'
import { notifyErrorCustom } from '../../../utils/notify'

import { motion } from 'framer-motion'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ForgotPasswordService } from '../../../services'
import { ArrowRigth } from '../../../assets/svgs/icons'

const CODE_LENGTH = 6
const RESEND_SECONDS = 30

function StepVerifyEmail({ email, setStep, setCode, code }) {
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [resendSeconds, setResendSeconds] = useState(RESEND_SECONDS)
  const inputRefs = useRef([])
  const navigate = useNavigate()

  const STEPS = {
    STEP_EMAIL: 'stepEmail',
    STEP_PASS_PASSWORD: 'stepPassword',
  }

  const normalizedCode = code.replace(/\D/g, '').slice(0, CODE_LENGTH)
  const codeDigits = Array.from({ length: CODE_LENGTH }, (_, index) =>
    normalizedCode[index] || ''
  )

  useEffect(() => {
    if (code !== normalizedCode) {
      setCode(normalizedCode)
    }
  }, [code, normalizedCode, setCode])

  useEffect(() => {
    if (resendSeconds === 0) {
      return undefined
    }

    const timer = window.setInterval(() => {
      setResendSeconds((currentSeconds) => currentSeconds - 1)
    }, 1000)

    return () => window.clearInterval(timer)
  }, [resendSeconds])

  const focusInput = (index) => {
    const input = inputRefs.current[index]

    if (input) {
      input.focus()
      input.select()
    }
  }

  const updateCodeValue = (nextDigits) => {
    setCode(nextDigits.join('').slice(0, CODE_LENGTH))
  }

  const handleDigitChange = (index, rawValue) => {
    const digits = rawValue.replace(/\D/g, '').split('')

    if (digits.length === 0) {
      const nextDigits = [...codeDigits]
      nextDigits[index] = ''
      updateCodeValue(nextDigits)
      return
    }

    const nextDigits = [...codeDigits]

    digits.forEach((digit, digitIndex) => {
      const targetIndex = index + digitIndex

      if (targetIndex < CODE_LENGTH) {
        nextDigits[targetIndex] = digit
      }
    })

    updateCodeValue(nextDigits)

    if (index + digits.length < CODE_LENGTH) {
      focusInput(index + digits.length)
    }
  }

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace') {
      event.preventDefault()
      const nextDigits = [...codeDigits]

      if (nextDigits[index]) {
        nextDigits[index] = ''
        updateCodeValue(nextDigits)
        return
      }

      if (index > 0) {
        nextDigits[index - 1] = ''
        updateCodeValue(nextDigits)
        focusInput(index - 1)
      }

      return
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault()
      focusInput(index - 1)
    }

    if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      event.preventDefault()
      focusInput(index + 1)
    }
  }

  const handlePaste = (index, event) => {
    event.preventDefault()

    const pastedDigits = event.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, CODE_LENGTH - index)

    if (!pastedDigits) {
      return
    }

    const nextDigits = [...codeDigits]

    pastedDigits.split('').forEach((digit, digitIndex) => {
      nextDigits[index + digitIndex] = digit
    })

    updateCodeValue(nextDigits)
    focusInput(Math.min(index + pastedDigits.length, CODE_LENGTH - 1))
  }

  const handleNavigate = async (type) => {
    if (type === 'next') {
      const response = await ForgotPasswordService.postVerifyCode({
        email,
        code,
      })

      // console.log('response', response)

      if (response.responseStatus === 200) {
        setStep(STEPS.STEP_PASS_PASSWORD)
      } else {
        notifyErrorCustom(
          t('forgotPasswordStepCode.forgot_password_code_error')
        )
      }

      setIsLoading(false)
    } else {
      setStep(STEPS.STEP_EMAIL)
    }
  }

  const handleResendCode = async () => {
    if (resendSeconds > 0 || isResending) {
      return
    }

    setIsResending(true)

    const response = await ForgotPasswordService.postsendCode({
      email,
    })

    setIsResending(false)

    if (response.responseStatus === 200) {
      setCode('')
      setResendSeconds(RESEND_SECONDS)
      focusInput(0)
    } else {
      notifyErrorCustom(
        t('forgotPasswordStepCode.forgot_password_code_error')
      )
    }
  }

  const handleBackToLogin = () => {
    navigate('/SignIn')
  }

  const { t } = useTranslation()
  const formattedResendSeconds = String(resendSeconds).padStart(2, '0')
  return (
    <motion.div
      initial={{ x: '100vw' }}
      animate={{ x: 0 }}
      exit={{ x: '-100vw' }}
      transition={{ type: 'tween', duration: 0.5 }}
      className={styles.step_motion}
    >
      <div className={styles.box_step}>
        <div className={styles.box_page}>
          <h1 className={styles.step_title}>
            Verificação de código
          </h1>

          <div className={styles.box_input_email}>
            <p className={styles.step_description}>
              Enviamos um código de 6 dígitos para {email || '{email}'}. Insira
              abaixo para continuar.
            </p>

            <div className={styles.otp_group}>
              {codeDigits.map((digit, index) => (
                <input
                  key={`otp-${index}`}
                  ref={(element) => {
                    inputRefs.current[index] = element
                  }}
                  className={`${styles.otp_input} ${
                    digit ? styles.otp_input_filled : ''
                  }`}
                  type="text"
                  inputMode="numeric"
                  autoComplete={index === 0 ? 'one-time-code' : 'off'}
                  maxLength={1}
                  value={digit}
                  onChange={(event) => handleDigitChange(index, event.target.value)}
                  onPaste={(event) => handlePaste(index, event)}
                  onKeyDown={(event) => handleKeyDown(index, event)}
                  aria-label={`Dígito ${index + 1} do código de verificação`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className={styles.box_footer}>
          <button
            type="button"
            disabled={isLoading || normalizedCode.length < CODE_LENGTH}
            className={styles.next_button}
            onClick={() => {
              setIsLoading(true)
              handleNavigate('next')
            }}
          >
            <span style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
              <span>Continuar</span>
              <ArrowRigth size={24} />
            </span>
          </button>

          <p className={styles.resend_prompt}>
            Não recebeu o código?{' '}
            <button
              type="button"
              className={styles.resend_link}
              disabled={resendSeconds > 0 || isResending}
              onClick={handleResendCode}
            >
              {resendSeconds > 0
                ? `Reenviar em 00:${formattedResendSeconds}`
                : isResending
                  ? 'Reenviando...'
                  : 'Reenviar'}
            </button>
          </p>

          <div className={styles.divider} />

          <p className={styles.login_prompt}>
            Lembrou da senha?{' '}
            <button
              type="button"
              className={styles.login_link}
              onClick={handleBackToLogin}
            >
              Voltar ao login
            </button>
          </p>
        </div>
      </div>
    </motion.div>
  )
}

export default StepVerifyEmail
